/**
 * Single source of truth for per-`waterfall_strategy` UI metadata.
 *
 * Drives the four radio cards in the Configure modal (spec §5), the
 * "Strategy" column on the Enrollments table (spec §8.2), and the strategy
 * chip on the enrollment detail drawer header (spec §9.4). Every component
 * that needs a label, a description sentence, or an icon for a given
 * strategy goes through `WATERFALL_META[enrollment.waterfall_strategy]` so
 * the four surfaces stay aligned.
 *
 * Refs:
 * - docs/superpowers/specs/2026-05-07-outreach-05-enrollment-design.md §5
 *   (waterfall strategy picker)
 * - docs/superpowers/specs/2026-05-07-outreach-05-enrollment-design.md §16.2
 *   (waterfall resolution rules)
 * - All `IconName`s verified against
 *   `frontend/node_modules/@central-icons-react/all/icons/index.d.ts`.
 */

import type { IconName } from "@/components/ui/icon";

import type { WaterfallStrategy } from "./types";

export interface WaterfallMeta {
  value: WaterfallStrategy;
  /** Card title in the radio picker. */
  label: string;
  /**
   * One-sentence explanation of which contact channel runs first and what
   * happens when the preferred channel is missing. Renders as the supporting
   * copy under each radio card and as the tooltip body on the Enrollments
   * table chip.
   */
  description: string;
  /** Centralicons name for the strategy icon (verified). */
  icon: IconName;
}

export const WATERFALL_META: Record<WaterfallStrategy, WaterfallMeta> = {
  prefer_personal_fallback_professional: {
    value: "prefer_personal_fallback_professional",
    label: "Personal first, fall back to professional",
    description:
      "Try the candidate's personal email first; if none is on file, use their professional email.",
    icon: "IconUserHeart",
  },
  prefer_professional_fallback_personal: {
    value: "prefer_professional_fallback_personal",
    label: "Professional first, fall back to personal",
    description:
      "Try the candidate's professional email first; if none is on file, use their personal email.",
    icon: "IconBuildings",
  },
  only_professional: {
    value: "only_professional",
    label: "Professional only",
    description:
      "Only enroll candidates with a professional email — skip anyone without one.",
    icon: "IconMailbox",
  },
  only_personal: {
    value: "only_personal",
    label: "Personal only",
    description:
      "Only enroll candidates with a personal email — skip anyone without one.",
    icon: "IconUser",
  },
};

/**
 * Canonical strategy ordering used in the Configure modal radio list (spec
 * §5 — "Personal first" is the default and renders top, the two single-channel
 * variants render last because they're the most restrictive).
 */
export const WATERFALL_ORDER: WaterfallStrategy[] = [
  "prefer_personal_fallback_professional",
  "prefer_professional_fallback_personal",
  "only_professional",
  "only_personal",
];

/**
 * Default selected strategy when the Configure modal opens with no prior
 * selection. Mirrors backend's `EnrollmentService` default (spec §16.2).
 */
export const WATERFALL_DEFAULT: WaterfallStrategy =
  "prefer_personal_fallback_professional";
